export {Website as Page};

import {Helmet} from "react-helmet-async";
import {useEffect, useState, useContext} from "preact/hooks";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import {ContentLocked} from "../../../components/ContentLocked.jsx";
import messages from "../../../messages/messages.js";
import API_URL from "../../../includes/api.js";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import {useAuth} from '../../../components/AuthProvider.jsx';
import {CircleSpinner, RingSpinner} from '../../../components/Spinner.jsx';

function Website() {

    const socials = ["youtube", "twitter", "patreon", "facebook", "instagram", "discord", "tiktok", "soundcloud", "rumble", "spotify"];

    const {addNotification} = useContext(NotificationContext);
    const {getToken, user, authLoading} = useAuth();

    const [allowed, setAllowed] = useState(false);
    const [active, setActive] = useState(false);
    const [fields, setFields] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [newDomain, setNewDomain] = useState('');

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => {
            fetch(API_URL + "/website/get?firebase_auth_token=" + token)
                .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setAllowed(true);
                    setActive(true);
                    setFields(data.data);
                } else if (data.code === 201) {
                    setAllowed(true);
                    setActive(false);
                }
            }).finally(() => {
                setPageLoading(false);
            });
        });
    }, [authLoading, user])

    const activateSite = async () => {
        setLoading(true);
        const token = await getToken();
        fetch(API_URL + "/website/activate?firebase_auth_token=" + token + "&domain_name=" + newDomain, {
            method: "POST"
        }).then(response => response.json()).then(data => {
            if (data.code === 200) {
                setActive(true);
                addNotification('Your site has been activated!', 'success');
                setFields({...fields, domain_name: newDomain});
            } else {
                addNotification(data.message, 'error');
            }
        }).catch((error) => {
            addNotification(error.message, 'error');
        }).finally(() => {
            setLoading(false);
            window.scrollTo({top: 0, behavior: 'smooth'});
        });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = await getToken();
        const formData = new FormData();
        let items = ["domain_name", "name", "subtitle", "form_mail", "about_me", "social_text"];
        socials.forEach(social => {
            items.push(social);
        });
        items.forEach(field => {
            const value = e?.target[field]?.value;
            if (value) {
                formData.append(field, value);
            }
        });
        formData.append("firebase_auth_token", token);

        fetch(API_URL + "/website/save", {
            method: "POST", body: formData
        }).then(response => response.json()).then(data => {
            if (data.code === 200) {
                addNotification('Your website has been updated!', 'success');
            } else {
                setTimeout(() => {
                    addNotification(data.message, 'error');
                }, 1000);
            }
        }).catch((error) => {
            addNotification(error.message, 'error');
        }).finally(() => {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        });

    }
    const handleChange = (e) => {
        setFields({...fields, [e.target.name]: e.target.value});
    }

    return (<section className="text-white">

        <Helmet>
            <title>{messages["SITE.NAME"]} | My subscriber website</title>
            <meta name="description" content="My subscriber website"/>
        </Helmet>

        <div>

            <div className="flex flex-wrap">

                <AccountSidebar/>

                {pageLoading ? (<div className="w-full md:w-3/4 px-0 md:px-4">
                    <div className="p-6 text-white rounded-lg bg-zinc-800">
                        <div className="flex items-center">
                            <CircleSpinner/>
                            <h3 className="text-lg m-0">Loading...</h3>
                        </div>
                    </div>
                </div>) : (!allowed ? (<div className="w-full md:w-3/4 px-0 md:px-4">
                    <ContentLocked/>
                </div>) : (<div className="w-full md:w-3/4 px-0 md:px-4">
                    <div className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-3">
                        {!active ? (
                            <div>
                                <h3 className="text-xl mt-0">My website</h3>
                                <p className="text-sm text-gray-400">
                                    Your site has not been activated yet. Please contact an administrator through
                                    discord by opening a ticket in our <a href={messages.DISCORD_URL}>discord
                                    server</a> to help you through this process or activate it yourself by choosing a
                                    domain name and clicking the button below.
                                </p>
                                {!loading ? (
                                    <form method="get" onSubmit={(e) => e.preventDefault()}>
                                        <div className="flex flex-wrap -mx-3 mb-6">
                                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                                <label className="block mb-2 text-sm font-medium text-white">Domain
                                                    name</label>
                                                <input type="text" name="domain_name" id="domain_name"
                                                       value={newDomain}
                                                       onChange={(e) => setNewDomain(e.target.value)}
                                                       className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                       placeholder={"EXAMPLE." + messages["DOMAIN"]}
                                                       required/>
                                            </div>
                                        </div>
                                        <button onClick={() => activateSite()}
                                                className="text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-10 py-2.5 text-center">Activate
                                        </button>
                                    </form>
                                ) : (
                                    <button
                                        className="text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-10 py-2.5 text-center">Activating...</button>
                                )}
                            </div>
                        ) : (<>
                            <div className="flex justify-between">
                                <h3 className="text-xl mt-0">My website</h3>
                                {fields.domain_name && (
                                    <a href={`https://${fields.domain_name}.${messages["DOMAIN"]}`}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       className="text-white flex items-center gap-2">
                                        <span>Go to website</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" height="17px"
                                             className="fill-white"
                                             viewBox="0 0 512 512">
                                            <path
                                                d="M352 256c0 22.2-1.2 43.6-3.3 64H163.3c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64H348.7c2.2 20.4 3.3 41.8 3.3 64zm28.8-64H503.9c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64H380.8c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32H376.7c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0H167.7c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0H18.6C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192H131.2c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64H8.1C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6H344.3c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352H135.3zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6H493.4z"/>
                                        </svg>
                                    </a>)}
                            </div>
                            <form onSubmit={handleSubmit}>

                                <p className="text-sm text-gray-400">
                                    Here you can update your website information. This includes your site name,
                                    subtitle, email address, about me text and social media links.
                                    <br/>
                                    <b><i>If you would like to change the banner image, please contact an
                                        administrator through discord by opening a ticket in our <a
                                            href={messages.DISCORD_URL}>discord
                                            server</a>.</i></b>
                                </p>

                                <div className="flex flex-wrap -mx-3 mb-6">
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label htmlFor="domain_name"
                                               className="block mb-2 text-sm font-medium text-white">Domain name</label>
                                        <input type="text" name="domain_name" id="domain_name"
                                               value={fields.domain_name || ""}
                                               onChange={handleChange}
                                               placeholder={"EXAMPLE." + messages["DOMAIN"]}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               required/>
                                    </div>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label htmlFor="name"
                                               className="block mb-2 text-sm font-medium text-white">Name</label>
                                        <input type="text" name="name" id="name"
                                               value={fields.name || ""}
                                               onChange={handleChange}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               placeholder="Site name" required/>
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-3 mb-6">
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label htmlFor="subtitle"
                                               className="block mb-2 text-sm font-medium text-white">Subtitle</label>
                                        <input type="text" name="subtitle" id="subtitle"
                                               value={fields.subtitle || ""}
                                               onChange={handleChange}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               placeholder="Site subtitle"/>
                                    </div>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label htmlFor="form_mail"
                                               className="block mb-2 text-sm font-medium text-white">Email
                                            address</label>
                                        <input type="email" name="form_mail" id="form_mail"
                                               value={fields.form_mail || ""}
                                               onChange={handleChange}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               placeholder="Email address"/>
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-3 mb-6">
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label htmlFor="about_me"
                                               className="block mb-2 text-sm font-medium text-white">About
                                            me</label>
                                        <textarea rows="4" name="about_me" id="about_me"
                                                  placeholder="About me text"
                                                  onChange={handleChange}
                                                  className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-xs border border-gray-300"
                                        >{fields.about_me || ""}</textarea>
                                    </div>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">

                                        <label htmlFor="social_text"
                                               className="block mb-2 text-sm font-medium text-white">Social
                                            text</label>
                                        <textarea rows="4" name="social_text" id="social_text"
                                                  placeholder="Social text"
                                                  onChange={handleChange}
                                                  className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-xs border border-gray-300"
                                        >{fields.social_text || ""}</textarea>
                                    </div>
                                </div>

                                <h3 className="text-xl mt-0">Socials</h3>
                                <div className="flex flex-wrap -mx-3">
                                    {socials.map((social) => {
                                        let name = social.charAt(0).toUpperCase() + social.slice(1);
                                        if (name === "Twitter") {
                                            name = "X";
                                        }
                                        return (<div className="w-full md:w-1/3 px-3 mb-6">
                                            <label htmlFor={social}
                                                   className="block mb-2 text-sm font-medium text-white">
                                                {name}
                                            </label>
                                            {social === "youtube" ? (
                                                <input type="url" name={social} id={social}
                                                       value={fields[social]}
                                                       onChange={handleChange}
                                                       className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                       placeholder={name + " URL"}
                                                       required
                                                />
                                            ) : (
                                                <input type="url" name={social} id={social}
                                                       value={fields[social]}
                                                       onChange={handleChange}
                                                       className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                       placeholder={name + " URL"}
                                                />
                                            )}
                                        </div>)
                                    })}
                                </div>

                                <button type="submit"
                                        className="text-white cursor-pointer border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-10 py-2.5 text-center">Save {loading && (
                                    <RingSpinner/>)}
                                </button>

                            </form>
                        </>)}
                    </div>
                </div>))}
            </div>
        </div>
    </section>);
}
